import { Button } from '@finpay-development/shared-components';
import { authDocType } from '@finpay-development/shared-platform/lib/esm/shared/enums';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    showErrorStatus,
    showStatus,
} from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import { AppDispatch } from '../../../shared/state/store';
import { getAuthDocTemplates, selectAuthDoc } from '../../../shared/utils/auth-doc-utils';
import { estimateService } from '../../services/estimate-service';

interface SidebarPatientSummaryOptionsProps {
    clientId: number;
    facilityId: number;
    patientEncounterId: number;
    estimateId: number;
    // Sync IOC pfrAmt (and related fields) from the current estimate before send.
    // Resolves true when IOC was updated; false when the sync was skipped.
    onBeforeSendSummary?: () => Promise<boolean>;
}

export function SidebarPatientSummaryOptions({
    clientId,
    facilityId,
    patientEncounterId,
    estimateId,
    onBeforeSendSummary,
}: SidebarPatientSummaryOptionsProps) {
    const dispatch = useDispatch<AppDispatch>();
    const [hasEstTemplate, setHasEstTemplate] = useState(false);
    const [isSending, setIsSending] = useState(false);

    const handlePreviewSummary = () => {
        window.open(`/estimate/v5/${estimateId}`, '_blank');
    };

    useEffect(() => {
        setHasEstTemplate(false);
        if (!clientId) return;

        let isActive = true;
        getAuthDocTemplates(clientId).then((response) => {
            if (!isActive) return;
            if (response.hasErrors) {
                console.error('Error retrieving client auth docs');
                return;
            }
            setHasEstTemplate(
                !!selectAuthDoc(response.entity ?? [], clientId, facilityId, authDocType.est)
            );
        });
        return () => {
            isActive = false;
        };
    }, [clientId, facilityId]);

    const handleSendSummary = async () => {
        setIsSending(true);
        try {
            let didUpdateIoc = false;
            if (onBeforeSendSummary) {
                try {
                    didUpdateIoc = await onBeforeSendSummary();
                } catch (error) {
                    console.error(error);
                    dispatch(
                        showErrorStatus(
                            'Failed to update Instance of Care before sending estimate summary'
                        )
                    );
                    return;
                }
            }

            const response = await estimateService.sendEstimateSummary(patientEncounterId);
            if (response.hasErrors) {
                dispatch(
                    showErrorStatus(
                        response.entity?.message ||
                            response.errorMessage ||
                            'Failed to send estimate summary'
                    )
                );
            } else {
                dispatch(
                    showStatus(
                        didUpdateIoc
                            ? 'Successfully updated Instance of Care and sent estimate summary to patient'
                            : 'Successfully sent estimate summary to patient'
                    )
                );
            }
        } finally {
            setIsSending(false);
        }
    };

    if (!hasEstTemplate) return null;

    return (
        <Box
            border={1}
            borderColor="primary.main"
            p={2}
            style={{ backgroundColor: 'white', marginBottom: '1.5em' }}
            test-id="patient-summary-options"
        >
            <Typography variant="subtitle2" test-id="patient-summary-options-title">
                Patient Summary Options
            </Typography>
            <Box display="flex" justifyContent="space-around" gap={1} pt={2}>
                <Button
                    onClick={handlePreviewSummary}
                    disabled={!estimateId}
                    test-id="patient-summary-options-preview-button"
                >
                    Preview
                </Button>
                <Button
                    onClick={handleSendSummary}
                    disabled={isSending}
                    test-id="patient-summary-options-send-button"
                >
                    {isSending ? 'Sending...' : 'Send'}
                </Button>
            </Box>
        </Box>
    );
}
